import { instance } from "api/apiBase";
import { normalizeFilename, normalizeFile } from "shared/utils";

interface FileInfoType {
  dataURL?: string | null;
  name: string;
  size: number;
  type: string;
}

function addNameToDataURL(dataURL: string, name: string) {
  if (!dataURL) return null;

  return dataURL.replace(";base64", `;name=${encodeURIComponent(name)};base64`);
}

export function processFile(file: File): Promise<FileInfoType> {
  const { name, size, type } = file;
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onerror = reject;
    reader.onload = (event) => {
      resolve({
        dataURL:
          typeof event.target?.result === "string"
            ? addNameToDataURL(event.target.result, name)
            : null,
        name,
        size,
        type
      });
    };
    reader.readAsDataURL(file);
  });
}

export function svgToBlob(svgContent: string) {
  return new Blob([svgContent], { type: "image/svg+xml" });
}

export function svgToPngBlob(svgContent: string): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const svgBlob = svgToBlob(svgContent);
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { alpha: true });

      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else resolve(svgBlob);
        }, "image/png");
      } else {
        URL.revokeObjectURL(url);
        resolve(svgBlob);
      }
    };

    img.src = url;
  });
}

const MIME_MAP: Record<string, string> = {
  "image/webp": "image/webp",
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
  "image/jpg": "image/jpeg",
  "image/svg+xml": "image/png"
};

const COLOR_TOLERANCE = 30;
const ALPHA_THRESHOLD = 10;

type RGBA = [number, number, number, number];

const getPixel = (data: Uint8ClampedArray, i: number): RGBA => [
  data[i],
  data[i + 1],
  data[i + 2],
  data[i + 3]
];

const areSimilar = (a: RGBA, b: RGBA) =>
  Math.abs(a[0] - b[0]) <= COLOR_TOLERANCE &&
  Math.abs(a[1] - b[1]) <= COLOR_TOLERANCE &&
  Math.abs(a[2] - b[2]) <= COLOR_TOLERANCE &&
  Math.abs(a[3] - b[3]) <= COLOR_TOLERANCE;

const isBackgroundPixel = (data: Uint8ClampedArray, i: number, bg: RGBA) => {
  if (data[i + 3] < ALPHA_THRESHOLD) return true;
  if (bg[3] < ALPHA_THRESHOLD) return false;
  return (
    Math.abs(data[i] - bg[0]) <= COLOR_TOLERANCE &&
    Math.abs(data[i + 1] - bg[1]) <= COLOR_TOLERANCE &&
    Math.abs(data[i + 2] - bg[2]) <= COLOR_TOLERANCE
  );
};

const sampleEdgePixels = (
  data: Uint8ClampedArray,
  width: number,
  height: number
): RGBA[] => {
  const step = Math.max(1, Math.floor(Math.max(width, height) / 20));
  const pixels: RGBA[] = [];

  Array.from({ length: Math.ceil(width / step) }).forEach((_, i) => {
    const x = Math.min(i * step, width - 1);
    pixels.push(getPixel(data, x * 4));
    pixels.push(getPixel(data, ((height - 1) * width + x) * 4));
  });
  Array.from({ length: Math.ceil(height / step) }).forEach((_, i) => {
    const y = Math.min(i * step, height - 1);
    pixels.push(getPixel(data, y * width * 4));
    pixels.push(getPixel(data, (y * width + width - 1) * 4));
  });
  return pixels;
};

const detectBackground = (
  data: Uint8ClampedArray,
  width: number,
  height: number
): RGBA => {
  const samples = sampleEdgePixels(data, width, height);
  let bestColor = samples[0];
  let bestCount = 0;

  samples.forEach((sample) => {
    const count = samples.filter((s) => areSimilar(s, sample)).length;
    if (count > bestCount) {
      bestCount = count;
      bestColor = sample;
    }
  });

  return bestColor;
};

const BLOCK_SIZE = 16;
const MIN_BLOCK_PIXELS = 1;

const floodFillGrid = (
  grid: Uint8Array,
  cols: number,
  rows: number,
  start: number
): number[] => {
  const cluster: number[] = [];
  const queue = [start];
  grid[start] = 0;

  // Iterative instead of recursive to avoid stack overflow on large images
  while (queue.length > 0) {
    const idx = queue.pop()!;
    cluster.push(idx);
    const r = Math.floor(idx / cols);
    const c = idx % cols;

    [-1, 1, -cols, cols].forEach((offset) => {
      const ni = idx + offset;
      if (ni < 0 || ni >= rows * cols || grid[ni] !== 1) return;
      const nr = Math.floor(ni / cols);
      const nc = ni % cols;
      if (Math.abs(nr - r) + Math.abs(nc - c) !== 1) return;
      grid[ni] = 0;
      queue.push(ni);
    });
  }

  return cluster;
};

const findLargestCluster = (
  contentGrid: Uint8Array,
  cols: number,
  rows: number
): number[] => {
  const grid = new Uint8Array(contentGrid);
  let largest: number[] = [];

  Array.from({ length: rows * cols }).forEach((_, i) => {
    if (grid[i] !== 1) return;
    const cluster = floodFillGrid(grid, cols, rows, i);
    if (cluster.length > largest.length) largest = cluster;
  });

  return largest;
};

const getContentBounds = (imageData: ImageData) => {
  const { data, width, height } = imageData;
  const bg = detectBackground(data, width, height);

  const cols = Math.ceil(width / BLOCK_SIZE);
  const rows = Math.ceil(height / BLOCK_SIZE);
  const blockCounts = new Uint32Array(rows * cols);

  Array.from({ length: height }).forEach((_, y) => {
    const blockRow = Math.floor(y / BLOCK_SIZE);
    Array.from({ length: width }).forEach((_, x) => {
      if (!isBackgroundPixel(data, (y * width + x) * 4, bg))
        blockCounts[blockRow * cols + Math.floor(x / BLOCK_SIZE)]++;
    });
  });

  const contentGrid = new Uint8Array(rows * cols);
  Array.from({ length: rows * cols }).forEach((_, i) => {
    if (blockCounts[i] >= MIN_BLOCK_PIXELS) contentGrid[i] = 1;
  });

  const largest = findLargestCluster(contentGrid, cols, rows);
  if (largest.length === 0) return null;

  const bounds = { top: height, bottom: 0, left: width, right: 0 };

  largest.forEach((idx) => {
    const br = Math.floor(idx / cols);
    const bc = idx % cols;
    const blockTop = br * BLOCK_SIZE;
    const blockLeft = bc * BLOCK_SIZE;
    const blockBottom = Math.min(blockTop + BLOCK_SIZE - 1, height - 1);
    const blockRight = Math.min(blockLeft + BLOCK_SIZE - 1, width - 1);

    if (blockTop < bounds.top) bounds.top = blockTop;
    if (blockBottom > bounds.bottom) bounds.bottom = blockBottom;
    if (blockLeft < bounds.left) bounds.left = blockLeft;
    if (blockRight > bounds.right) bounds.right = blockRight;
  });

  return bounds;
};

export const trimImageWhitespace = (file: File): Promise<File> =>
  // eslint-disable-next-line compat/compat -- Promise already used in processFile and svgToPngBlob above
  new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file); // eslint-disable-line compat/compat -- already used in svgToPngBlob above, only unsupported in Opera Mini
    const cleanup = () => URL.revokeObjectURL(url); // eslint-disable-line compat/compat

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });

      if (!ctx) {
        cleanup();
        return resolve(file);
      }

      ctx.drawImage(img, 0, 0);
      const bounds = getContentBounds(
        ctx.getImageData(0, 0, canvas.width, canvas.height)
      );

      if (!bounds) {
        cleanup();
        return resolve(file);
      }

      const w = bounds.right - bounds.left + 1;
      const h = bounds.bottom - bounds.top + 1;
      const trimmed = document.createElement("canvas");
      trimmed.width = w;
      trimmed.height = h;
      const tCtx = trimmed.getContext("2d");

      if (!tCtx) {
        cleanup();
        return resolve(file);
      }

      tCtx.drawImage(canvas, bounds.left, bounds.top, w, h, 0, 0, w, h);

      const mimeType = MIME_MAP[file.type] || "image/jpeg";
      trimmed.toBlob((blob) => {
        cleanup();
        if (!blob) return resolve(file);
        resolve(new File([blob], file.name, { type: mimeType }));
      }, mimeType);
    };

    img.onerror = () => {
      cleanup();
      resolve(file);
    };

    img.src = url;
  });

export const getPresignedUrl = async (file: File) => {
  const queryParams = new URLSearchParams({
    filename: normalizeFilename(file.name),
    type: file.type
  }).toString();

  const { data } = await instance(`/portal/v1/assets/presign?${queryParams}`);
  return data.url;
};

export const uploadFileToS3Bucket = (s3Url: string, fileObj: File) => {
  return fetch(s3Url, {
    method: "PUT",
    body: normalizeFile(fileObj),
    mode: "cors"
  });
};

export const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(",");
  const header = arr[0];
  const mime = header.slice(header.indexOf(":") + 1, header.indexOf(";"));
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

export const createAssetRecord = async (url: string, file: File) => {
  try {
    const { data } = await instance.post("/portal/v1/assets", {
      url,
      metadata: {
        original_filename: normalizeFilename(file.name),
        content_type: file.type,
        size: file.size
      }
    });
    return data.asset.global_id;
  } catch (error) {
    console.error("Error creating asset record:", error);
    throw error;
  }
};
